import React, {useState, useEffect} from 'react';
import {WebStorageStateStore} from 'oidc-client-ts';
import {AuthProvider} from 'react-oidc-context';
import App from './App';
import {PowerSyncDatabase} from '@powersync/web';
import {PowerSyncContext} from '@powersync/react';
import {PowerSyncUserProvider} from './Providers/PowerSyncUserProvider';
import {PowerSyncDataProvider} from './Providers/PowerSyncDataProvider';
import {connectPowerSync, setupPowerSync} from './PowerSync';
import LocalStorageService from './Services/LocalStorageService';
import {ROLES, STORAGE_KEYS} from './Constants/AppConstants';
import ApiService from './Services/ApiService';
import {AxiosRequestConfig} from 'axios';
import Colors from './Styles/Shared/Colors';
import {Modal, View} from 'react-native';
import ImpersonateOrg from './Components/Views/ImpersonateOrg';
import GeneralHelperService from './Services/GeneralHelperService';
import {IGeneralHelperService} from './Services/Interfaces/IGeneralHelperService';
import useEHSAuth from './Hooks/useEHSAuth';
import LoadingSpinner from './Components/Shared/LoadingSpinner';
import InvalidSubscription from './Components/Views/LoginError';
import AuthenticationService from './Services/AuthenticationService';
import LoginErrorPage from './Components/Views/LoginError';
import LoginError from './Components/Views/LoginError';

function AppWrapper(): React.ReactElement {
  const helperService: IGeneralHelperService = new GeneralHelperService();

  const [powerSyncReady, setPowerSyncReady] = useState(false);
  const [powerSync, setPowerSync] = useState<PowerSyncDatabase | null>(null);
  const [isImpersonator, setIsImpersonator] = useState(false);
  const [hasInvalidSubscription, setHasInvalidSubscription] = useState(false);
  const [invalidRoles, setInvalidRoles] = useState(false);

  const [impersonated, setIsImpersonated] = useState(false);
  const {
    isLoading,
    isAuthenticated,
    signinRedirect,
    authError,
    getUser,
    signoutRedirect,
  } = useEHSAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      signinRedirect();
    }
  }, [isLoading, isAuthenticated, authError, signinRedirect]);

  useEffect(() => {
    const setupPowerSyncCallback = async (clearDb: boolean) => {
      await setEncryptionKey();
      if (clearDb) await powerSync?.disconnectAndClear();
      await initPowerSync();
    };

    const authenticatedCallback = async () => {
      if (isAuthenticated) {
        let storageEmail = localStorage.getItem(STORAGE_KEYS.EMAIL);
        let authEmail = getUser().profile.sub;

        let authService = new AuthenticationService();
        let hasAccess = await authService.hasAccessToFeature();

        if (!hasAccess) {
          setHasInvalidSubscription(true);
          return;
        }

        let currentRoles = helperService.getCurrentUserRoles();
        let impersonating = currentRoles.some(
          x => x == ROLES.IMPERSONATOR_USER,
        );

        if (impersonating && !impersonated) {
          setIsImpersonator(impersonating);
          return;
        }

        let hasFIRS = currentRoles.some(x => Object.values(ROLES).includes(x));
        setInvalidRoles(!hasFIRS);

        let clearDb = false;
        if (storageEmail != authEmail) clearDb = true;
        setupPowerSyncCallback(clearDb);
        localStorage.setItem(STORAGE_KEYS.EMAIL, authEmail ?? '');
      }
    };

    authenticatedCallback();
  }, [isAuthenticated, impersonated]);

  const initPowerSync = async () => {
    try {
      let newPowerSync = await setupPowerSync();
      await connectPowerSync(newPowerSync!, logoutCallback);
      setPowerSync(newPowerSync);
      setPowerSyncReady(true);
    } catch (err) {
      //Error setting up powersync logout
      signoutRedirect();
      console.log(err);
    }
  };

  const logoutCallback = async () => {
    let storageService = new LocalStorageService();
    storageService.set(STORAGE_KEYS.OPSQLITE_ENCRYPTION_KEY, '');
    setPowerSyncReady(false);
    await signoutRedirect();
    setIsImpersonator(false);
  };

  //Impersonation success, set up powersync and by logging in
  const impersonateCallback = () => {
    setIsImpersonated(true);
  };

  const setEncryptionKey = async () => {
    let opsqliteKey = '';
    let apiService = new ApiService(() => {});

    let url =
      process.env.REACT_APP_VAULT_URI +
      '?names[0]=' +
      process.env.REACT_APP_SQLITE_DB_ENCRYPTION_KEY;
    let config: AxiosRequestConfig = {};
    config.method = 'POST';
    config.url = url;

    try {
      let reponse = await apiService.sendRequest<any>(config);
      if (
        reponse.data &&
        reponse.data[process.env.REACT_APP_SQLITE_DB_ENCRYPTION_KEY!]
      )
        opsqliteKey =
          reponse.data[process.env.REACT_APP_SQLITE_DB_ENCRYPTION_KEY!];

      let storageService = new LocalStorageService();
      storageService.set(STORAGE_KEYS.OPSQLITE_ENCRYPTION_KEY, opsqliteKey);
    } catch (err) {
      console.log(err);
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return <LoadingSpinner message="Loading" visible={isLoading} />;
    }

    if (hasInvalidSubscription) {
      return <LoginError error="You don't have access to FIRS." />;
    }

    if (invalidRoles) {
      return <LoginError error="Missing roles for FIRS." />;
    }

    //If impersonator but not logged in yet show impersonation page
    if (!impersonated && isImpersonator) {
      return <ImpersonateOrg impersonateCallback={impersonateCallback} />;
    }

    if (!powerSyncReady) return <View> </View>;
    return (
      <PowerSyncContext.Provider value={powerSync!}>
        <PowerSyncUserProvider>
          <PowerSyncDataProvider>
            <App logoutCallback={logoutCallback} />
          </PowerSyncDataProvider>
        </PowerSyncUserProvider>
      </PowerSyncContext.Provider>
    );
  };

  return (
    <div style={{height: '100vh', backgroundColor: Colors.darkestGreen}}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" />
      <link
        href="https://fonts.googleapis.com/css2?family=Poppins:wght@100;200;300;400;500;600;700;800;900&display=swap"
        rel="stylesheet"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Lato:wght@100;300;400;700;900&family=Poppins:wght@100;200;300;400;500;600;700;800;900&display=swap"
        rel="stylesheet"></link>
      {renderBody()}
    </div>
  );
}

export default AppWrapper;
